import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, FileText } from "lucide-react";
import { toast } from "sonner";

const HIDE_ON = ["/terms", "/privacy", "/auth", "/forgot-password", "/reset-password", "/activate"];

export function TermsGate() {
  const { user } = useAuth();
  const location = useLocation();
  const [needs, setNeeds] = useState(false);
  const [agree, setAgree] = useState(false);
  const [saving, setSaving] = useState(false);

  const hidden = HIDE_ON.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    if (!user || hidden) { setNeeds(false); return; }
    api.get("/policies/current").then((r) => setNeeds(!!r.data.needs_acceptance)).catch(() => {});
  }, [user, location.pathname, hidden]);

  if (!user || hidden || !needs) return null;

  const accept = async () => {
    if (!agree) return;
    setSaving(true);
    try {
      await api.post("/policies/accept");
      setNeeds(false);
      toast.success("Thanks — you're all set");
    } catch {
      toast.error("Couldn't record your acceptance. Please try again.");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="terms-gate" className="fixed inset-0 z-[100] flex items-center justify-center bg-zinc-900/60 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-zinc-900 text-white"><FileText className="h-5 w-5" /></div>
        <h2 className="mt-4 text-center font-display text-xl font-bold text-zinc-900">Nearfluence Terms</h2>
        <p className="mt-2 text-center text-sm text-zinc-500">By continuing, you confirm that you agree to the Nearfluence Terms of Service and acknowledge the Privacy Policy.</p>

        <label className="mt-5 flex cursor-pointer items-start gap-3 rounded-2xl border border-zinc-200 p-4">
          <Checkbox data-testid="terms-gate-checkbox" checked={agree} onCheckedChange={(v) => setAgree(!!v)} className="mt-0.5" />
          <span className="text-sm text-zinc-700">I agree to the Nearfluence Terms of Service and acknowledge the Privacy Policy.</span>
        </label>

        <div className="mt-3 flex items-center justify-center gap-4 text-sm">
          <Link to="/terms" target="_blank" data-testid="terms-gate-view-terms" className="font-semibold text-emerald-700 hover:underline">View Terms of Service</Link>
          <span className="text-zinc-300">·</span>
          <Link to="/privacy" target="_blank" data-testid="terms-gate-view-privacy" className="font-semibold text-emerald-700 hover:underline">View Privacy Policy</Link>
        </div>

        <button data-testid="terms-gate-accept" onClick={accept} disabled={!agree || saving}
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-emerald-600 py-3 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accept & Continue"}
        </button>
      </div>
    </div>
  );
}
